export type SettledTaskClaimsStore = {
  /** Registers a listener, called whenever a claim is taken or released. Returns an unsubscribe. */
  subscribe: (listener: () => void) => () => void;
  /** Changes on every claim and release, and only then, so it is safe as a React snapshot. */
  getVersion: () => number;
  hasSettledTask: (remoteTaskId: string) => boolean;
  /** Takes the claim, or returns false if it is already held. */
  claimTaskSettlement: (remoteTaskId: string) => boolean;
  releaseTaskSettlement: (remoteTaskId: string) => void;
  /** Claims without notifying — for seeding during render, where the reader is the render itself. */
  seedSettledTasks: (remoteTaskIds: string[]) => void;
};

/**
 * The remote tasks one `useSharedSyncTasks` instance has settled.
 *
 * It has to be readable synchronously from async callbacks — a claim is an atomic check-and-add
 * against the newest set, and a stale snapshot would let one task settle twice — so it cannot live
 * in React state. It is a store instead, so `useSettledTaskClaims` can subscribe React to it: what
 * is claimed decides whether an instance still has anything to poll for.
 */
export const createSettledTaskClaims = (): SettledTaskClaimsStore => {
  const settledRemoteTaskIds = new Set<string>();
  const listeners = new Set<() => void>();
  let version = 0;

  const emit = () => {
    version += 1;
    listeners.forEach((listener) => {
      listener();
    });
  };

  return {
    subscribe: (listener) => {
      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    },
    getVersion: () => version,
    hasSettledTask: (remoteTaskId) => settledRemoteTaskIds.has(remoteTaskId),
    claimTaskSettlement: (remoteTaskId) => {
      if (settledRemoteTaskIds.has(remoteTaskId)) {
        return false;
      }

      settledRemoteTaskIds.add(remoteTaskId);
      emit();

      return true;
    },
    releaseTaskSettlement: (remoteTaskId) => {
      if (settledRemoteTaskIds.delete(remoteTaskId)) {
        emit();
      }
    },
    seedSettledTasks: (remoteTaskIds) => {
      remoteTaskIds.forEach((remoteTaskId) => {
        settledRemoteTaskIds.add(remoteTaskId);
      });
    },
  };
};
